import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://greenhillmetrics.com/api';

// Global token callbacks registry (can be set by AuthContext)
let globalCallbacks = null;

/**
 * Register AuthContext token callbacks globally
 * Call this from AuthContext or an effect to enable context-aware token management
 * @param {Object} callbacks - Token management callbacks
 * @param {Function} callbacks.getToken - Function to get the access token
 * @param {Function} callbacks.setToken - Function to set the access token
 * @param {Function} callbacks.getRefreshToken - Function to get the refresh token
 * @param {Function} callbacks.clearTokens - Function to clear both tokens
 */
export const registerTokenCallbacks = (callbacks) => {
  globalCallbacks = callbacks;
};

/**
 * Setup axios interceptors for token management
 * @param {Object} config - Configuration object (optional)
 * @param {Function} config.getToken - Function to get the access token
 * @param {Function} config.setToken - Function to set the access token
 * @param {Function} config.getRefreshToken - Function to get the refresh token
 * @param {Function} config.clearTokens - Function to clear both tokens
 * @returns {AxiosInstance} Configured axios instance
 */
/**
 * Create a lazy-loaded axios instance that's created on first use
 * This ensures globalCallbacks is registered before the instance is created
 * @param {Object} config - Configuration object (optional)
 * @returns {AxiosInstance} Configured axios instance
 */
export const createLazyApiInstance = (config = {}) => {
  let api = null;

  return new Proxy({}, {
    get: (target, prop) => {
      // Create instance on first access
      if (!api) {
        api = createApiInstance(config);
      }
      return api[prop];
    }
  });
};

export const createApiInstance = (config = {}) => {
  // Use registered callbacks if available, otherwise use provided config, otherwise use localStorage
  const callbacks = globalCallbacks || config;
  
  const {
    getToken = () => localStorage.getItem('accessToken'),
    setToken = (token) => localStorage.setItem('accessToken', token),
    getRefreshToken = () => localStorage.getItem('refreshToken'),
    clearTokens = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  } = callbacks;

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Interceptor to add JWT token to requests
  api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Interceptor to handle token refresh on 401
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = getRefreshToken();
          if (refreshToken) {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken
            });

            const { accessToken } = response.data;
            setToken(accessToken);

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          clearTokens();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
};
